import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header } from 'semantic-ui-react';
import LoaderComponent from 'components/LoaderComponent';
import styled from 'styled-components';
import { BillStatus, BillType, StornoType } from 'ts-types/api.enums';
import { BillSearchResponse, BillStornoRequest, PatientDto } from 'ts-types/api.types';
import { BillIconRequest, getBillIcon } from 'components/bill/utils/billUtils';
import arrayMutators from 'final-form-arrays';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import moment from 'moment';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import { AxiosError, CancelTokenSource } from 'axios';
import axios from 'services/http';
import { stornoBills } from 'services/stornoBillServices';
import ErrorMessage from 'components/final-form/ErrorMessage';
import DatePicker from 'components/final-form/DatePicker';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { FormApi, FormState } from 'final-form';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import { RouteComponentProps } from 'react-router';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContent from 'components/layout/FormContent';
import PxGrid from 'components/layout/PxGrid';
import { applyStyles } from 'utils/textUtils';
import { numberFormatter } from 'utils/fomattingUtils';

const StyledFinalFormContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1em 1em;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__rowColumn:nth-child(5) {
    text-align: right;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-flow: column;
  height: 100%;
`;

const StyledFormContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
  margin-bottom: 15px;
  height: 100%;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }

  .header-data {
    margin-top: 0.2rem;
    font-weight: 300;
  }

  .styled-row-data {
    margin-bottom: 1rem;
  }
`;

const StyledFormButtonsRow = styled(FormButtonsRow)`
  margin-top: 1rem;
`;

const TableContainer = styled.div`
  height: 100%;
  min-height: 300px;
`;

const StyledHeader = styled(Header)`
  margin-top: 0 !important;
`;

const StyledSelectedBills = styled.span`
  float: right;
  text-align: right;
  font-weight: 300;
  font-size: 1rem;

  &.margin-top {
    margin-top: 0.5em;
  }

  .highlight-value {
    color: rgb(48 145 218);
    font-weight: bold;
  }
`;

const errorMessageStyle = {
  marginBottom: "1rem"
};

const activeRowStyle = {
  background: "#e0e0e0",
  color: "rgba(0,0,0,.87)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
};

interface ExtendedBillStornoSearchResponse extends BillSearchResponse {
  cancel: boolean
}

interface FinalFormData {
  bills: Array<ExtendedBillStornoSearchResponse>,
  selected?: ExtendedBillStornoSearchResponse,
  stornoDate: string
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  bills: Array<BillSearchResponse>,
  currentStornoType: StornoType,
  billsLoading: boolean,
  currentPatient?: PatientDto,
  onPatientRefresh: (patient?: PatientDto) => void
}

interface State {
  cancelTokenSource: CancelTokenSource,
  formData: Partial<FinalFormData>
}

class BillStornoTableView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {options: {workDate}} = this.props;

    this.state = {
      cancelTokenSource: axios.CancelToken.source(),
      formData: {
        stornoDate: workDate
      }
    }
  }

  componentDidUpdate({billsLoading: prevBillsLoading}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {formData} = this.state;
    const {bills, billsLoading} = this.props;

    if (prevBillsLoading != billsLoading && !billsLoading) {

      const state: any = this.props.location.state;
      const selectedBillId = (state && state.selectedBill) ? state.selectedBill.id : undefined;

      const mappedBills: Array<ExtendedBillStornoSearchResponse> = bills.map(bill => ({
        ...bill,
        cancel: !!selectedBillId
      }));

      this.setState({
        formData: {
          stornoDate: formData.stornoDate,
          bills: mappedBills,
          selected: undefined
        }
      })
    }
  }

  updateBillToBeCanceled = (bill: ExtendedBillStornoSearchResponse, index: number, form: FormApi) => {
    if (bill.status != BillStatus.STORNO) {

      const modifiedBill: ExtendedBillStornoSearchResponse = {
        ...bill,
        cancel: !bill.cancel
      };

      form.change(`bills[${index}]`, modifiedBill);
    }
  };

  handleChange = ({values}: FormState) => {
    const {formData: {bills: prevBills = [], stornoDate}} = this.state;

    const bills: Array<ExtendedBillStornoSearchResponse> = values.bills ? values.bills : [];

    let shouldUpdate = false;
    if (prevBills.length == bills.length) {
      for (let i = 0; i < prevBills.length; i++) {
        if (prevBills[i].id != bills[i].id || prevBills[i].cancel != bills[i].cancel) {
          shouldUpdate = true;
          break;
        }
      }
    }

    if (stornoDate != values.stornoDate || prevBills.length != bills.length || shouldUpdate) {
      this.setState({
        formData: {
          ...values
        }
      })
    }
  };

  handleSubmit = async (values: Partial<FinalFormData>) => {

    const {options: {workDate}, currentPatient} = this.props;
    const {bills = [], stornoDate = workDate} = values;

    if (bills.length > 0) {

      const {cancelTokenSource} = this.state;
      const {currentStornoType, history, getMessage} = this.props;

      const billIds = bills
          .filter(bill => bill.cancel)
          .map(bill => bill.id);

      if (billIds.length > 0) {
        const billStornoRequest: BillStornoRequest = {
          billIds: billIds,
          stornoDate: stornoDate,
          stornoType: currentStornoType
        };

        let errorMessage: any = undefined;
        const refreshPatient = () => this.props.onPatientRefresh(currentPatient);
        await stornoBills(billStornoRequest, cancelTokenSource)
            .then(() => {
              history.push("/");
              if (currentPatient) {
                refreshPatient();
              }
            })
            .catch((error: AxiosError) => {
              const {response} = error;

              if (response && response.data) {
                const {violations} = response.data;

                if (violations && violations.length > 0) {
                  violations.forEach((violation: any) => {
                    if (violation.errorCode == "invoice-xml-error") {
                      errorMessage = {
                        errorHeader: getMessage("error.xmlGeneration"),
                        errorContent: violation.details.split("\n")
                      };
                    }
                  });
                }
              }

              if (errorMessage == undefined) {
                errorMessage = getMessage("error.general");
              }
            });

        if (errorMessage != undefined) {
          return {
            error: errorMessage
          }
        }
      } else {
        return {
          error: getMessage("error.noStornoBillsSelected")
        }
      }
    }
  };

  billStornoCellRenderer = (data: any) => {

    const {rowIndex, rowData} = data;

    return (
        <Field
            name={`bills[${rowIndex}].cancel`}
            component={CheckBox}
            disabled={rowData.status === BillStatus.STORNO || rowData.type === BillType.PART_OF_COLLECTIVE}
        />
    )
  };

  billStornoSelectHeader = () => {

    const {formData} = this.state;
    const foundBills = formData.bills ? formData.bills : [];
    const cancellableBills = foundBills.filter(bill => bill.status !== BillStatus.STORNO);
    const countCancellableBills = cancellableBills.length;
    const countSelectedBills = foundBills.filter(bill => bill.cancel).length;
    const indeterminate = countSelectedBills > 0 && countSelectedBills < countCancellableBills;
    const noSelectedBills = countSelectedBills === 0 && countCancellableBills > 0;
    const allBillsSelected = countCancellableBills > 0 && countSelectedBills === countCancellableBills;

    return (
        <CheckboxSc
            checked={countSelectedBills > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedBills || indeterminate) {
                  let bills = [...foundBills];
                  const selectedBills = bills.map(bill => {
                    if (bill.status !== BillStatus.STORNO) {
                      return {...bill, cancel: true};
                    }
                    return bill;
                  });
                  this.setState({
                    formData: {
                      bills: selectedBills
                    }
                  });
                }

                if (allBillsSelected) {
                  let bills = [...foundBills];
                  const selectedBills = bills.map(b => ({...b, cancel: false}));
                  this.setState({
                    formData: {
                      bills: selectedBills
                    }
                  });
                }

              }
            }
            disabled={countCancellableBills === 0}
        />
    )
  }

  billStatusCellRenderer = (data: any) => {

    const rowData: ExtendedBillStornoSearchResponse = data.rowData;

    const billIconRequest: BillIconRequest = {
      hasReminders: false,
      status: rowData.status as BillStatus,
      type: rowData.type as BillType,
      cashBill: rowData.cashBill
    };

    return <div>{getBillIcon(billIconRequest)}</div>
  };

  billDateCellRenderer = (data: any) => <div>{moment(data.rowData.date).format("DD.MM.YYYY")}</div>;
  amountCellRenderer = (data: any) => <div>{(data.rowData.amount)}</div>;

  billsRowGetter = (bills: Array<ExtendedBillStornoSearchResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  billsRowRenderer = (form: FormApi) => ({className, columns, index, key, style}: any) => {
    const a11yProps = {"aria-rowindex": index + 1};

    const {bills = [], selected}: Partial<FinalFormData> = form.getState().values;

    let styles = {...style};

    if (selected && bills[index].id == selected.id) {
      styles = {
        ...styles,
        ...activeRowStyle
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => form.change("selected", bills[index])}
            onDoubleClick={() => this.updateBillToBeCanceled(bills[index], index, form)}
        >
          {columns}
        </div>
    );
  };

  render(): React.ReactNode {
    const {formData} = this.state;
    return (
        <StyledFinalFormContainer>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              mutators={{
                ...arrayMutators
              }}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
        </StyledFinalFormContainer>
    )
  };

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {bills = []} = values;
    const {billsLoading, getMessage, getMessageWithNamedParams} = this.props;

    let stornoBills = 0;
    let amount = 0;

    const billsLength = bills.length;

    if (billsLength > 0) {

      const billsToBeCanceled = bills.filter((bill: ExtendedBillStornoSearchResponse) => bill.cancel);

      stornoBills = billsToBeCanceled.length;
      amount = billsToBeCanceled
          .map((bill: ExtendedBillStornoSearchResponse) => bill.amount)
          .reduce((amount1: number, amount2: number) => amount1 + amount2, 0);
    }

    const disabledBtn = stornoBills < 1;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContent>
            <StyledFormContent>
              <StyledContainer style={{width: "60%"}}>
                <Field
                    name="error"
                    component={ErrorMessage}
                    style={errorMessageStyle}
                />
                <StyledHeader>
                  {getMessage("bills")}
                  <StyledSelectedBills>
                    {
                      applyStyles(getMessage("bills.selectedStornoBills")
                          .replace("{{stornoBills}}", "" + stornoBills)
                          .replace("{{billsLength}}", "" + billsLength)
                      )
                    }
                  </StyledSelectedBills>
                </StyledHeader>

                <TableContainer>
                  {billsLoading
                      ? <LoaderComponent
                          message={`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}/>
                      : this.renderTable(form, bills)}
                </TableContainer>
                <Header as="h3" textAlign="right">
                  {
                    getMessageWithNamedParams("bills.numberOfStornoBills", {
                      stornoBills: stornoBills,
                      amount: numberFormatter.format(amount)
                    })
                  }
                </Header>
              </StyledContainer>
              <StyledContainer style={{width: "40%"}}>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column width={3} verticalAlign="middle">{getMessage("stornoDate")}:</Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          name="stornoDate"
                          component={DatePicker}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("patient")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row className={"styled-row-data"}>
                    <Grid.Column>
                      {values.selected &&
                      <Header as="h4" className={"header-data"}>
                        <div>{`${values.selected.patientName}, ${values.selected.patientFirstName}`}</div>
                        <div>{`${values.selected.patientCity}, ${values.selected.patientBirthDate}`}</div>
                      </Header>
                      }
                    </Grid.Column>
                  </Grid.Row>
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("guarantor")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row className={"styled-row-data"}>
                    <Grid.Column>
                      {values.selected && values.selected.guarantorDescription &&
                      <Header as="h4" className={"header-data"}>
                        <div>{`${values.selected.guarantorDescription}`}</div>
                        <div>{`${values.selected.guarantorZip}, ${values.selected.guarantorCity}`}</div>
                      </Header>
                      }
                    </Grid.Column>
                  </Grid.Row>
                </PxGrid>
              </StyledContainer>
            </StyledFormContent>
          </FormContent>
          <StyledFormButtonsRow>
            <Button compact type="submit" primary disabled={disabledBtn || submitting}>
              {getMessage("bill.status.storno")}
            </Button>
          </StyledFormButtonsRow>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </StyledForm>
    );
  };

  renderTable = (form: FormApi, bills: Array<ExtendedBillStornoSearchResponse>): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer(form)}
            rowCount={bills.length}
            columns={[
              {
                width: 100,
                label: this.billStornoSelectHeader(),
                dataKey: "cancel",
                cellRenderer: this.billStornoCellRenderer
              },
              {
                width: 70,
                label: getMessage("bill"),
                dataKey: "billStatus",
                cellRenderer: this.billStatusCellRenderer
              },
              {
                width: 70,
                label: "#",
                dataKey: "id"
              },
              {
                width: 100,
                label: getMessage("bill.date"),
                dataKey: "billDate",
                cellRenderer: this.billDateCellRenderer
              },
              {
                width: 440,
                label: getMessage("value"),
                dataKey: "amount",
                cellRenderer: this.amountCellRenderer
              }
            ]}
        />
    )
  };
}

const BillStornoTableViewWrapper =
    withAuthContext(
        withMessages(
            withRouterWorkaround(
                BillStornoTableView)));

export default withAuthContext(withRouterWorkaround(withMessages(BillStornoTableView)));
